import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useToast } from '@asad/crane-ui'

import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { clearMfaPending, doMfaVerify, doSendEmailMfaCode } from '@/store/slices/auth-slice'

import { MFA_RESEND_COOLDOWN_SECONDS, isMfaChallengeExhausted, mapMfaError } from '../mfa-utils'
import { useCountdown } from './useCountdown'

type MfaVerifyStep = 'select' | 'totp' | 'email' | 'backup'

export const useMfaVerify = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const toast = useToast()

  const mfaPending = useAppSelector((state) => state.auth.mfaPending)
  const hasEmail = mfaPending.availableMethods.includes('EMAIL')
  const hasMultipleMethods = mfaPending.availableMethods.length >= 2

  const emailJustSent = (location.state as { emailJustSent?: boolean } | null)?.emailJustSent === true

  const getInitialStep = (): MfaVerifyStep => {
    if (hasMultipleMethods) return 'select'
    if (hasEmail) return 'email'
    return 'totp'
  }

  const [step, setStep] = React.useState<MfaVerifyStep>(getInitialStep)
  const [prevStep, setPrevStep] = React.useState<'totp' | 'email' | null>(null)
  const [code, setCode] = React.useState('')
  const [backupCode, setBackupCode] = React.useState('')
  const [verifyError, setVerifyError] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isSendingEmail, setIsSendingEmail] = React.useState(false)
  const [resendSecondsLeft, setResendSecondsLeft] = useCountdown(emailJustSent ? MFA_RESEND_COOLDOWN_SECONDS : 0)

  const sendEmailAndGoToStep = async () => {
    if (resendSecondsLeft > 0) {
      setStep('email')
      return
    }
    setIsSendingEmail(true)
    try {
      await dispatch(doSendEmailMfaCode(i18n.language)).unwrap()
      setResendSecondsLeft(MFA_RESEND_COOLDOWN_SECONDS)
      toast.success(t('mfaVerify.emailSent'))
      setStep('email')
    } catch (error) {
      toast.error(mapMfaError(error, t))
    } finally {
      setIsSendingEmail(false)
    }
  }

  const resendEmailCode = async () => {
    if (resendSecondsLeft > 0 || isSendingEmail) return
    setIsSendingEmail(true)
    try {
      await dispatch(doSendEmailMfaCode(i18n.language)).unwrap()
      setResendSecondsLeft(MFA_RESEND_COOLDOWN_SECONDS)
      toast.success(t('mfaVerify.emailSent'))
    } catch (error) {
      toast.error(mapMfaError(error, t))
    } finally {
      setIsSendingEmail(false)
    }
  }

  const handleVerify = async (method: 'TOTP' | 'EMAIL' | 'BACKUP_CODE', value: string) => {
    if (!value.trim() || isSubmitting) return
    setVerifyError('')
    setIsSubmitting(true)
    try {
      await dispatch(doMfaVerify({ code: value.trim(), method })).unwrap()
      toast.success(t('beNotifications.success.loginSuccess'))
      navigate('/', { replace: true })
    } catch (error) {
      // Nowe wyzwanie powstaje wyłącznie w signIn(), więc jedyne wyjście to formularz logowania.
      // Powód leci przez sessionStorage — tym samym kanałem co `autoLoggedOut`.
      if (isMfaChallengeExhausted(error)) {
        sessionStorage.setItem('mfaAttemptsExhausted', '1')
        dispatch(clearMfaPending())
        navigate('/auth/login', { replace: true })
        return
      }
      setVerifyError(mapMfaError(error, t))
      if (method === 'BACKUP_CODE') setBackupCode('')
      else setCode('')
    } finally {
      setIsSubmitting(false)
    }
  }

  const goToBackup = (from: 'totp' | 'email') => {
    setPrevStep(from)
    setCode('')
    setVerifyError('')
    setStep('backup')
  }

  const goToSelect = () => {
    setCode('')
    setVerifyError('')
    setStep('select')
  }

  const goToTotpFromSelect = () => {
    setStep('totp')
    setVerifyError('')
  }

  const goBackFromBackup = () => {
    setStep(prevStep ?? 'totp')
    setBackupCode('')
    setVerifyError('')
  }

  const wasValidLogin = React.useRef(mfaPending.type === 'LOGIN')
  React.useEffect(() => {
    if (!wasValidLogin.current) {
      navigate('/auth/login', { replace: true })
    }
  }, [navigate])

  return {
    step,
    code,
    setCode,
    backupCode,
    setBackupCode,
    verifyError,
    isSubmitting,
    isSendingEmail,
    resendSecondsLeft,
    hasMultipleMethods,
    sendEmailAndGoToStep,
    resendEmailCode,
    handleVerify,
    goToBackup,
    goToSelect,
    goToTotpFromSelect,
    goBackFromBackup,
  }
}
